import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  isWorldBuildDeployed,
  WORLD_BASE_URL,
  WORLD_LOADER_URL,
} from '../lib/world/registry';
import type { PeakWorldMessage } from '../lib/world/registry';

/**
 * /world — host page for the Peak Priorities World Unity WebGL build.
 *
 * This page is deliberately NOT a world: no SVG map, no fake districts. It hosts the real
 * engine when a build is deployed under public/mountain-world/, and says so honestly when
 * one isn't (see apps/mountain-world/README.md for the build path). Bridge protocol:
 * apps/mountain-world/TECH_PLAN.md §3.
 */

interface UnityInstance {
  SendMessage: (gameObject: string, method: string, value?: string) => void;
  SetFullscreen: (fullscreen: number) => void;
  Quit: () => Promise<void>;
}

interface UnityConfig {
  dataUrl: string;
  frameworkUrl: string;
  codeUrl: string;
  streamingAssetsUrl: string;
  companyName: string;
  productName: string;
  productVersion: string;
  devicePixelRatio?: number;
}

declare global {
  interface Window {
    createUnityInstance?: (
      canvas: HTMLCanvasElement,
      config: UnityConfig,
      onProgress?: (progress: number) => void
    ) => Promise<UnityInstance>;
  }
}

type HostState = 'probing' | 'no-build' | 'loading' | 'running' | 'failed';

export function PeakWorld() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const instanceRef = useRef<UnityInstance | null>(null);
  const [state, setState] = useState<HostState>('probing');
  const [progress, setProgress] = useState(0);
  const [currentLand, setCurrentLand] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleMessage = useCallback(
    (event: Event) => {
      const detail = (event as CustomEvent).detail as PeakWorldMessage | undefined;
      if (!detail || typeof detail.type !== 'string') return;
      switch (detail.type) {
        case 'world/ready':
          setState('running');
          break;
        case 'world/land-entered':
          setCurrentLand(detail.payload.name);
          break;
        case 'route/open': {
          const href = detail.payload.href;
          if (href.startsWith('/')) navigate(href);
          else window.open(href, '_blank', 'noopener');
          break;
        }
        case 'world/error':
          setError(detail.payload.message);
          setState('failed');
          break;
        default:
          break;
      }
    },
    [navigate]
  );

  useEffect(() => {
    window.addEventListener('peakworld:message', handleMessage);
    return () => window.removeEventListener('peakworld:message', handleMessage);
  }, [handleMessage]);

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      const deployed = await isWorldBuildDeployed();
      if (cancelled) return;
      if (!deployed) {
        setState('no-build');
        return;
      }

      setState('loading');
      try {
        await injectScript(WORLD_LOADER_URL);
        if (cancelled || !canvasRef.current || !window.createUnityInstance) {
          if (!window.createUnityInstance) throw new Error('Unity loader did not register createUnityInstance');
          return;
        }
        const instance = await window.createUnityInstance(
          canvasRef.current,
          {
            dataUrl: `${WORLD_BASE_URL}/Build/PeakWorld.data`,
            frameworkUrl: `${WORLD_BASE_URL}/Build/PeakWorld.framework.js`,
            codeUrl: `${WORLD_BASE_URL}/Build/PeakWorld.wasm`,
            streamingAssetsUrl: `${WORLD_BASE_URL}/StreamingAssets`,
            companyName: 'Peak Priorities',
            productName: 'Peak Priorities World',
            productVersion: '0.1.0',
            devicePixelRatio: window.devicePixelRatio,
          },
          (p) => setProgress(p)
        );
        if (cancelled) {
          void instance.Quit();
          return;
        }
        instanceRef.current = instance;
        // 'running' flips on world/ready; keep 'loading' until then
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setState('failed');
        }
      }
    }

    void boot();
    return () => {
      cancelled = true;
      if (instanceRef.current) {
        void instanceRef.current.Quit();
        instanceRef.current = null;
      }
    };
  }, []);

  const travelTo = (landId: string) => {
    instanceRef.current?.SendMessage('WorldBridge', 'ReceiveMessage', JSON.stringify({ type: 'travel-to', landId }));
  };

  const engineVisible = state === 'loading' || state === 'running';

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0B0F19' }}>
      {/* chrome */}
      <div
        className="flex items-center justify-between px-6 py-3 border-b"
        style={{ borderColor: '#1A2234' }}
      >
        <div className="flex items-center gap-4">
          <Link to="/elevation/home" className="text-sm hover:underline" style={{ color: '#9BA5BC' }}>
            ← Back
          </Link>
          <span
            className="font-mono text-xs uppercase tracking-wider"
            style={{ color: '#D8DDE8' }}
          >
            Peak Priorities World
          </span>
        </div>
        <div className="flex items-center gap-4">
          {currentLand && (
            <span className="font-mono text-xs uppercase tracking-wider" style={{ color: '#D4A017' }}>
              ◤ {currentLand}
            </span>
          )}
          {state === 'running' && (
            <>
              <button
                onClick={() => travelTo('base-camp')}
                className="font-mono text-xs uppercase tracking-wider px-3 py-1 rounded"
                style={{ color: '#9BA5BC', background: '#111827' }}
              >
                Base Camp
              </button>
              <button
                onClick={() => instanceRef.current?.SetFullscreen(1)}
                className="font-mono text-xs uppercase tracking-wider px-3 py-1 rounded"
                style={{ color: '#9BA5BC', background: '#111827' }}
              >
                Fullscreen
              </button>
            </>
          )}
        </div>
      </div>

      {/* engine viewport */}
      <div className="flex-1 relative">
        {engineVisible && (
          <canvas
            ref={canvasRef}
            id="peak-world-canvas"
            className="absolute inset-0 w-full h-full"
            style={{ background: '#0B0F19' }}
          />
        )}

        {state === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-80">
              <div className="font-mono text-xs uppercase tracking-wider mb-3 text-center" style={{ color: '#9BA5BC' }}>
                Loading world — {Math.round(progress * 100)}%
              </div>
              <div className="h-1 rounded" style={{ background: '#1A2234' }}>
                <div
                  className="h-1 rounded transition-all"
                  style={{ background: '#D4A017', width: `${Math.round(progress * 100)}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {state === 'probing' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-mono text-xs uppercase tracking-wider" style={{ color: '#6B7A96' }}>
              Checking for engine build…
            </span>
          </div>
        )}

        {state === 'no-build' && (
          <div className="absolute inset-0 flex items-center justify-center p-8">
            <div
              className="max-w-2xl p-8"
              style={{ background: '#0D1220', border: '1px solid #1A2234' }}
            >
              <div className="font-mono text-xs uppercase tracking-wider mb-2" style={{ color: '#D4A017' }}>
                Engine build not deployed
              </div>
              <h1 className="text-2xl mb-4" style={{ color: '#D8DDE8' }}>
                The world is real — its WebGL build isn&apos;t deployed here yet.
              </h1>
              <p className="mb-4 leading-relaxed" style={{ color: '#9BA5BC' }}>
                Peak Priorities World is a Unity 6 project at{' '}
                <code className="font-mono text-sm" style={{ color: '#D8DDE8' }}>
                  apps/mountain-world/unity/
                </code>
                . This page hosts its WebGL build; no build has been copied into{' '}
                <code className="font-mono text-sm" style={{ color: '#D8DDE8' }}>
                  public/mountain-world/
                </code>{' '}
                on this deployment. No fake map is shown in its place — that&apos;s the point.
              </p>
              <ol className="list-decimal list-inside space-y-2 mb-4" style={{ color: '#9BA5BC' }}>
                <li>
                  Open <code className="font-mono text-sm">apps/mountain-world/unity/</code> in Unity 6 (6000.0.x)
                </li>
                <li>
                  Run <span className="font-mono text-sm">Peak Priorities → Build WebGL</span> (or{' '}
                  <code className="font-mono text-sm">apps/mountain-world/scripts/build-webgl.sh</code>, or the{' '}
                  <code className="font-mono text-sm">mountain-world-webgl</code> GitHub workflow)
                </li>
                <li>
                  Copy the contents of <code className="font-mono text-sm">unity/Build/PeakWorld/</code> into{' '}
                  <code className="font-mono text-sm">public/mountain-world/</code> and reload
                </li>
              </ol>
              <p className="font-mono text-xs uppercase tracking-wider" style={{ color: '#6B7A96' }}>
                Docs: apps/mountain-world/README.md · WORLD_DESIGN.md · TECH_PLAN.md
              </p>
            </div>
          </div>
        )}

        {state === 'failed' && (
          <div className="absolute inset-0 flex items-center justify-center p-8">
            <div
              className="max-w-xl p-8"
              style={{ background: '#0D1220', border: '1px solid #1A2234' }}
            >
              <div className="font-mono text-xs uppercase tracking-wider mb-2" style={{ color: '#C0533A' }}>
                Engine failed to start
              </div>
              <p style={{ color: '#9BA5BC' }}>{error ?? 'Unknown error.'}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

let loaderPromise: Promise<void> | null = null;

function injectScript(src: string): Promise<void> {
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => {
      loaderPromise = null;
      reject(new Error(`Failed to load ${src}`));
    };
    document.body.appendChild(script);
  });
  return loaderPromise;
}
